import React, { createContext, useContext, useState, useCallback } from 'react';

interface Toast {
  id: string;
  type: 'success' | 'error' | 'info';
  message: string;
}

interface ToastContextType {
  showToast: (message: string, type: 'success' | 'error' | 'info') => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showInfo: (message: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((message: string, type: 'success' | 'error' | 'info') => {
    const id = Date.now().toString();
    const newToast = { id, type, message };

    setToasts(prev => [...prev, newToast]);

    // Auto-dismiss after 3 seconds
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3000);
  }, []);

  const showSuccess = useCallback((message: string) => {
    showToast(message, 'success');
  }, [showToast]);

  const showError = useCallback((message: string) => {
    showToast(message, 'error');
  }, [showToast]);

  const showInfo = useCallback((message: string) => {
    showToast(message, 'info');
  }, [showToast]);

  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const getToastClasses = (type: string) => {
    switch (type) {
      case 'success':
        return 'bg-success text-success-inverse border-success';
      case 'error':
        return 'bg-danger text-danger-inverse border-danger';
      case 'info':
        return 'bg-info text-info-inverse border-info';
      default:
        return 'bg-gray-100 text-gray-900 border-gray-300';
    }
  };

  const getToastIcon = (type: string) => {
    switch (type) {
      case 'success':
        return 'ki-check-circle';
      case 'error':
        return 'ki-information-2';
      case 'info':
        return 'ki-information';
      default:
        return 'ki-information';
    }
  };

  return (
    <ToastContext.Provider value={{ showToast, showSuccess, showError, showInfo }}>
      {children}

      {/* Toast Container */}
      <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`
              pointer-events-auto
              min-w-[300px] max-w-[500px]
              px-4 py-3 rounded-lg shadow-lg
              border-2
              flex items-center gap-3
              animate-in slide-in-from-top-2 fade-in duration-200
              ${getToastClasses(toast.type)}
            `}
          >
            <i className={`ki-filled ${getToastIcon(toast.type)} text-lg flex-shrink-0`}></i>
            <div className="flex-1 text-sm font-medium">
              {toast.message}
            </div>
            <button
              onClick={() => dismissToast(toast.id)}
              className="flex-shrink-0 hover:opacity-70 transition-opacity"
              aria-label="Dismiss"
            >
              <i className="ki-filled ki-cross text-sm"></i>
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
